<template>
  <Swiper
    v-if="shouldRenderCarousel"
    ref="swiperRef"
    :modules="[SwiperAutoplay]"
    v-bind="settings"
  >
    <slot />
    <template v-if="itemCount > 1" #container-start>
      <UniversalCarouselArrow side="left" :small="small" />
    </template>
    <template v-if="itemCount > 1" #container-end>
      <UniversalCarouselArrow side="right" :small="small" />
    </template>
  </Swiper>
</template>

<script setup lang="ts">
import { Swiper } from 'swiper/vue'
import { Autoplay as SwiperAutoplay } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'

const props = defineProps({
  active: {
    type: Boolean as PropType<boolean>,
    default: true,
  },
  scaleSlidesToTrackWidth: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  displayWithPadding: {
    type: Boolean as PropType<boolean>,
    default: true,
  },
  displayWithCustomPadding: {
    type: String as PropType<string>,
    default: '0px',
  },
  maxItemsPerSlide: {
    type: Number,
    default: 4,
  },
  maxItemsPerSlideMobile: {
    type: Number,
    default: 2,
  },
  small: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  centerMode: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  autoplaySpeed: {
    type: Number as PropType<number>,
    default: 0,
  },
  itemCount: {
    type: Number,
    default: 0,
  },
})
const slots = useSlots()
const { isMobile } = useDeviceType()

const carouselSetting = (itemCount: number) => {
  // Breaking point slide count
  const slidesToShowLargeScreen = ref<number>(
    itemCount < props.maxItemsPerSlide ? itemCount : props.maxItemsPerSlide,
  )
  const slidesToShowMediumScreen = computed<number>(
    () => slidesToShowLargeScreen.value,
  )
  const slidesToShowTabledScreen = computed<number>(() =>
    slidesToShowMediumScreen.value < 3
      ? slidesToShowMediumScreen.value
      : slidesToShowMediumScreen.value - 2,
  )
  const slidesToShowMobileScreen: number = props.maxItemsPerSlideMobile

  const getCenterPadding = (slidesToShow: number) => {
    const { displayWithPadding, displayWithCustomPadding } = props

    if (!displayWithPadding || !displayWithCustomPadding) {
      return '0px'
    }

    return (
      displayWithCustomPadding || (slidesToShow < itemCount ? '50px' : '0px')
    )
  }

  // Carousel settings
  return {
    enabled: props.active,
    autoHeight: true,
    // TODO: find alternative to centerPadding here and in breakpoints
    centerPadding: getCenterPadding(
      isMobile.value ? slidesToShowMobileScreen : slidesToShowLargeScreen.value,
    ),
    arrows: true,
    loop: true,
    slidesPerView: isMobile.value
      ? slidesToShowMobileScreen
      : slidesToShowLargeScreen.value,
    speed: 600,
    lazyLoad: 'onDemand',
    ...(props.autoplaySpeed && {
      autoplay: {
        delay: props.autoplaySpeed,
      },
    }),
    centeredSlides: props.centerMode,
    breakpoints: {
      1024: {
        centerPadding: getCenterPadding(slidesToShowMediumScreen.value),
        slidesPerView: slidesToShowMediumScreen.value,
      },
      768: {
        centerPadding: getCenterPadding(slidesToShowTabledScreen.value),
        slidesPerView: slidesToShowTabledScreen.value,
      },
      640: {
        centerPadding: getCenterPadding(slidesToShowMobileScreen),
        slidesPerView: slidesToShowMobileScreen,
      },
    },
    itemCount,
  }
}

const settings = ref(carouselSetting(props.maxItemsPerSlide))

onMounted(() => {
  if (slots.default && props.scaleSlidesToTrackWidth) {
    settings.value = carouselSetting(props.itemCount)
  }
})

const shouldRenderCarousel = computed(
  // Guard the first-vnode access: an empty default slot yields `[]`, so `[0]`
  // is undefined and `.children` would throw during render (ECOM-2690).
  () => slots.default?.()?.[0]?.children?.length,
)
</script>
